import _suggestNoticeCircleFill from "@zenlayer/zen-design-icons/assets/suggest/NoticeCircleFill.svg";
import _generalDiscountFill from "@zenlayer/zen-design-icons/assets/general/DiscountFill.svg";
import Icon from '../icon';
import Tag from '../tag';
import Tooltip from '../tooltip';
import Typography from '../typography';
import { useCreation, useMount, useUnmount } from 'ahooks';
import classnames from 'classnames';
import { uniqWith } from 'ramda';
import React, { useRef, useState, createElement as _createElement } from 'react';
import { isNone, isNumber } from '../_util/defineType';
import { calc, pickDomAttrs } from '../_util/utils';
import { getTransedText } from '../locale/getTransedText';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
export function ItemLabel(_ref) {
  let {
    label,
    discount,
    stockLimited,
    labelSuffix = [],
    localText
  } = _ref;
  const saleTagElement = useCreation(() => {
    if (isNumber(discount) && discount >= 0 && discount < 100) {
      return /*#__PURE__*/_jsx(Tag, {
        type: "warning",
        size: "xs",
        sale: true,
        block: true,
        children: getTransedText(localText.off, [calc.sub(100, discount)])
      });
    }
    if (discount === true) {
      return /*#__PURE__*/_jsx(Typography.Text, {
        type: "warning",
        children: /*#__PURE__*/_jsx(Icon, {
          type: "general/DiscountFill",
          block: true
        })
      });
    }
    return null;
  }, [discount, localText]);
  const stockLimitedTipElement = useCreation(() => {
    if (!stockLimited) {
      return null;
    }
    return /*#__PURE__*/_jsx(Tooltip, {
      title: localText.stockLimited,
      getPopupContainer: triggerNode => triggerNode.parentNode,
      children: /*#__PURE__*/_jsx(Icon, {
        type: "suggest/NoticeCircleFill",
        block: true
      })
    });
  }, [stockLimited, localText]);
  const suffixItems = useCreation(() => {
    const uniqItems = uniqWith((a, b) => a.index === b.index, labelSuffix);
    if (stockLimitedTipElement) {
      uniqItems.push({
        index: 10.1,
        element: stockLimitedTipElement
      });
    }
    if (saleTagElement) {
      uniqItems.push({
        index: 20.1,
        element: saleTagElement
      });
    }
    const elements = uniqItems.sort((a, b) => a.index - b.index).flatMap(item => {
      if (!item.hidden && item.element) {
        return [/*#__PURE__*/_jsx("div", {
          className: "label-suffix-item",
          children: item.element
        }, item.index)];
      }
      return [];
    });
    return elements;
  }, [labelSuffix, stockLimitedTipElement, saleTagElement]);
  return /*#__PURE__*/_jsxs("div", {
    className: "item-label",
    children: [/*#__PURE__*/_jsx("div", {
      className: "item-label-name",
      children: label
    }), suffixItems.length > 0 && /*#__PURE__*/_jsx("div", {
      className: "item-label-suffix",
      children: suffixItems
    })]
  });
}
const ZoneList = _ref2 => {
  let {
    value,
    zones,
    localText,
    onChange
  } = _ref2;
  const timerRef = useRef();
  const zoneWrapRef = useRef(null);
  const activeZoneEleRef = useRef(null);
  const [opacity, setOpacity] = useState(0);
  useMount(() => {
    // 滚动到当前选中项
    if (zones.some(c => c.key === value.zone.key)) {
      timerRef.current = setInterval(() => {
        const zoneReady = zones.some(z => z.key === value.zone.key) ? !!activeZoneEleRef.current && !!activeZoneEleRef.current.offsetHeight : true;
        if (zoneReady) {
          var _zoneWrapRef$current, _activeZoneEleRef$cur;
          if (timerRef.current) {
            clearInterval(timerRef.current);
          }
          (_zoneWrapRef$current = zoneWrapRef.current) === null || _zoneWrapRef$current === void 0 || _zoneWrapRef$current.scrollTo({
            top: (_activeZoneEleRef$cur = activeZoneEleRef.current) === null || _activeZoneEleRef$cur === void 0 ? void 0 : _activeZoneEleRef$cur.offsetTop
          });
          setOpacity(1);
        }
      }, 16);
    } else {
      setOpacity(1);
    }
  });
  useUnmount(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
  });
  return /*#__PURE__*/_jsx("div", {
    ref: zoneWrapRef,
    className: "column-inner",
    style: {
      position: 'relative',
      // 用于辅助 scrollTop 定位
      opacity
    },
    children: zones.map(zone => {
      const active = value.zone.key === zone.key;
      const attrs = pickDomAttrs(zone.data);
      return /*#__PURE__*/_createElement("div", {
        ...attrs.data,
        key: zone.key,
        ref: active ? activeZoneEleRef : undefined,
        className: classnames('column-item', {
          active,
          disabled: zone.disabled
        }),
        onClick: () => {
          if (!active && !zone.disabled) {
            onChange({
              ...value,
              zone
            });
          }
        }
      }, zone.labelRender ? zone.labelRender({
        active
      }) : /*#__PURE__*/_createElement(ItemLabel, {
        ...zone,
        key: zone.key,
        localText: localText
      }));
    })
  });
};
function Content(props) {
  const {
    value,
    cities,
    extraElement,
    zoneColumnWidth,
    showZoneColumn,
    localText,
    baseClassName,
    onChange
  } = props;
  const timerRef = useRef();
  const cityWrapRef = useRef(null);
  const activeCityEleRef = useRef(null);
  const [opacity, setOpacity] = useState(0);
  useMount(() => {
    // 滚动到当前选中项
    if (value && cities.some(c => c.key === value.city.key)) {
      timerRef.current = setInterval(() => {
        const cityReady = !!activeCityEleRef.current && !!activeCityEleRef.current.offsetHeight;
        if (cityReady) {
          var _cityWrapRef$current, _activeCityEleRef$cur;
          if (timerRef.current) {
            clearInterval(timerRef.current);
          }
          (_cityWrapRef$current = cityWrapRef.current) === null || _cityWrapRef$current === void 0 || _cityWrapRef$current.scrollTo({
            top: (_activeCityEleRef$cur = activeCityEleRef.current) === null || _activeCityEleRef$cur === void 0 ? void 0 : _activeCityEleRef$cur.offsetTop
          });
          setOpacity(1);
        }
      }, 16);
    } else {
      setOpacity(1);
    }
  });
  useUnmount(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
  });
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("div", {
      className: "".concat(baseClassName, "--column city"),
      children: /*#__PURE__*/_jsx("div", {
        ref: cityWrapRef,
        className: "column-inner",
        style: {
          position: 'relative',
          // 用于辅助 scrollTop 定位
          opacity
        },
        children: cities.map(city => {
          const active = city.key === (value === null || value === void 0 ? void 0 : value.city.key);
          const disabled = city.disabled === true;
          const attrs = pickDomAttrs(city.data);
          return /*#__PURE__*/_jsx(Tooltip, {
            title: city.tooltip,
            children: /*#__PURE__*/_jsx("div", {
              ...attrs.data,
              ref: active ? activeCityEleRef : undefined,
              className: classnames('column-item', {
                active,
                disabled
              }),
              onClick: () => {
                if (!active && !city.disabled) {
                  const first = city.children[0];
                  onChange({
                    ...first.ANCESTORS,
                    zone: first
                  });
                }
              },
              children: city.labelRender ? city.labelRender({
                active
              }) : /*#__PURE__*/_createElement(ItemLabel, {
                ...city,
                key: city.key,
                localText: localText
              })
            })
          }, city.key);
        })
      })
    }), showZoneColumn && !!value && /*#__PURE__*/_jsxs("div", {
      className: "".concat(baseClassName, "--column zone"),
      style: {
        width: zoneColumnWidth
      },
      children: [/*#__PURE__*/_jsx(ZoneList, {
        value: value,
        ...props
      }, value.city.key), !isNone(extraElement) && /*#__PURE__*/_jsx("div", {
        className: "extra-container",
        children: extraElement
      })]
    })]
  });
}
export default Content;